import React, { useState } from 'react';
import { XMarkIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import {
  CHANGE_CATEGORY_LABELS,
  type TemplateChangeCategory,
} from './templateManagementService';

interface TaskDiffSummary {
  added: string[];
  removed: string[];
  modified: string[];
}

interface ChangeReasonModalProps {
  templateName: string;
  taskDiff?: TaskDiffSummary;
  affectedVariantCount: number;
  onConfirm: (reason: string, category: TemplateChangeCategory) => void;
  onCancel: () => void;
  isSaving?: boolean;
}

const ChangeReasonModal: React.FC<ChangeReasonModalProps> = ({
  templateName,
  taskDiff,
  affectedVariantCount,
  onConfirm,
  onCancel,
  isSaving = false,
}) => {
  const [category, setCategory] = useState<TemplateChangeCategory | ''>('');
  const [reason, setReason] = useState('');

  const canSubmit = category !== '' && reason.trim().length > 0 && !isSaving;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onConfirm(reason.trim(), category as TemplateChangeCategory);
  };

  const hasDiff = taskDiff && (taskDiff.added.length > 0 || taskDiff.removed.length > 0 || taskDiff.modified.length > 0);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 dark:bg-black/70"
        onClick={onCancel}
        style={{ animation: 'lex-overlay-in 150ms ease-out forwards' }}
      />

      <div
        className="relative z-10 w-full max-w-lg mx-4 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden"
        style={{ animation: 'lex-modal-in 220ms ease-out forwards' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
              <DocumentTextIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Save Base Template Changes</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-[300px]">{templateName}</p>
            </div>
          </div>
          <button
            onClick={onCancel}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4 max-h-[65vh] overflow-y-auto">
          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Category <span className="text-red-500">*</span>
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as TemplateChangeCategory)}
              className="w-full outline-none px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-lex-accent transition-colors text-sm"
            >
              <option value="">Select category...</option>
              {Object.entries(CHANGE_CATEGORY_LABELS).map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </select>
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
              Reason <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Describe why this change is needed..."
              rows={3}
              className="w-full outline-none px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white bg-white dark:bg-gray-700 focus:ring-2 focus:ring-lex-accent transition-colors text-sm resize-none"
            />
          </div>

          {/* Change Summary */}
          {hasDiff && (
            <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-900/40 border border-gray-200 dark:border-gray-700">
              <p className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1.5">Change Summary</p>
              <div className="space-y-1 text-xs">
                {taskDiff.added.length > 0 && (
                  <p className="text-emerald-600 dark:text-emerald-400">
                    + {taskDiff.added.length} task{taskDiff.added.length > 1 ? 's' : ''} added
                    {taskDiff.added.length <= 3 && `: ${taskDiff.added.join(', ')}`}
                  </p>
                )}
                {taskDiff.removed.length > 0 && (
                  <p className="text-red-600 dark:text-red-400">
                    - {taskDiff.removed.length} task{taskDiff.removed.length > 1 ? 's' : ''} removed
                    {taskDiff.removed.length <= 3 && `: ${taskDiff.removed.join(', ')}`}
                  </p>
                )}
                {taskDiff.modified.length > 0 && (
                  <p className="text-amber-600 dark:text-amber-400">
                    ~ {taskDiff.modified.length} task{taskDiff.modified.length > 1 ? 's' : ''} modified
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Variant impact */}
          {affectedVariantCount > 0 && (
            <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
              <p className="text-xs text-amber-700 dark:text-amber-300">
                {affectedVariantCount} variant{affectedVariantCount > 1 ? 's' : ''} will inherit these changes on next instantiation.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <button
            onClick={onCancel}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChangeReasonModal;
